import Accordion from "@/common/components/Accordion";
import Box from "@/common/components/Box";
import ResumeItem from "@/common/components/ResumeItem";
import ReviewDescription from "@/common/components/ReviewDescription";
import { formatCurrency, formatDateTime } from "@/common/utils/formatters";
import { useForwardingTabContext } from "@/features/maintenance/features/workflows/hooks/forwardings/useForwardingTabContext";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { useQueryClient } from "@tanstack/react-query";
import { CarIcon } from "lucide-react";
import { useMemo } from "react";
import DirectiveEntryDiagnosticEntryPending from "../DiagnosticEntryPending";
import DiagnosticEntryObjectiveActivities from "./DiagnosticEntryObjectiveActivities";
import DiagnosticEntryObjectivePieces from "./DiagnosticEntryObjectivePieces";
import { MaintenanceForwardingDirectiveEntryStatus } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";

const DirectiveEntryDiagnosticEntryCompleted = () => {
  const { forwarding } = useForwardingTabContext<
    MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry
  >();

  const queryClient = useQueryClient();

  const forwardingState = queryClient.getQueryState(['maintenanceForwarding', forwarding.id]);

  const directiveEntryObjectivesSummary = useMemo(() => {
    let piecesCount = 0;
    let activitiesCount = 0;

    const piecesTotalValue = forwarding.directive_entry.objectives.reduce((prev, curr) => {
      return prev + (curr.diagnostic_pieces?.reduce((prevB, currB) => {
        piecesCount += 1;

        return prevB + parseFloat(currB.total_value);
      }, 0) || 0);
    }, 0);

    const actitiviesTotalValue = forwarding.directive_entry.objectives.reduce((prev, curr) => {
      return prev + (curr.diagnostic_activities?.reduce((prevB, currB) => {
        activitiesCount += 1;

        return prevB + parseFloat(currB.total_value);
      }, 0) || 0);
    }, 0);

    let piecesCountDisplay: string;

    if (piecesCount === 0) piecesCountDisplay = 'Nenhuma peça'
    else if (piecesCount === 1) piecesCountDisplay = '1 Peça';
    else piecesCountDisplay = `${piecesCount} Peças`;

    let activitiesCountDisplay: string;

    if (activitiesCount === 0) activitiesCountDisplay = 'Nenhum serviço'
    else if (activitiesCount === 1) activitiesCountDisplay = '1 Serviço';
    else activitiesCountDisplay = `${activitiesCount} Serviços`;

    return {
      piecesCount,
      piecesCountDisplay,
      piecesTotalValue,
      activitiesCount,
      activitiesCountDisplay,
      actitiviesTotalValue,
    }
  }, [forwardingState?.dataUpdatedAt]);

  const isDiagnosticEntryDisapproved =
    forwarding?.directive_entry?.last_review?.state === 'disapproved' &&
    forwarding?.directive_entry.status === MaintenanceForwardingDirectiveEntryStatus.InProgress;

  return (
    <>
      <Box className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1.5">
          <p className="font-semibold">
            {forwarding?.directive_entry?.id?.split('-')[0]?.toUpperCase()}
          </p>

          {isDiagnosticEntryDisapproved && (
            <ReviewDescription
              description={forwarding?.directive_entry?.last_review?.description}
            />
          )}

          <div className="flex gap-3">
            <ResumeItem
              label="Diagnóstico e orçamento enviado"
              value={
                forwarding?.directive_entry?.process_started_at
                  ? formatDateTime(new Date(forwarding?.directive_entry?.process_started_at))
                  : '--'
              }
              className="text-xs"
              valueClassName="font-bold text-sm"
            />

            {forwarding?.directive_entry?.completed_at && (
              <ResumeItem
                label="Aprovado em"
                value={formatDateTime(new Date(forwarding?.directive_entry?.completed_at))}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <ResumeItem
            label="Parecer técnico"
            value={forwarding.directive_entry?.technical_response}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />

          <div className="flex gap-3">
            <ResumeItem
              label="Prazo para execução do orçamento"
              value={`${forwarding.directive_entry?.deadline_to_complete_in_days} dias`}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />
          </div>

          <div className="flex gap-3">
            <ResumeItem
              label="Total orçado em peças"
              value={`${directiveEntryObjectivesSummary.piecesCountDisplay} • ${formatCurrency(directiveEntryObjectivesSummary.piecesTotalValue || 0)}`}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />

            <ResumeItem
              label="Total orçado em serviços"
              value={`${directiveEntryObjectivesSummary.activitiesCountDisplay} • ${formatCurrency(directiveEntryObjectivesSummary.actitiviesTotalValue || 0)}`}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />
          </div>

          <div className="flex gap-3">
            <ResumeItem
              label="Valor total do orçamento"
              value={formatCurrency(parseFloat(forwarding.directive_entry.total_value) || 0)}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-base font-bold">
            Orçamento
          </p>

          {forwarding.directive_entry.objectives.map((objective) => (
            <Accordion
              key={objective.id}
              defaultOpen
              className="p-0 rounded-lg"
              triggerClassName="p-4"
              bodyClassName="p-4 pt-0"
              head={(
                <div className="flex flex-col md:flex-row items-start md:items-center flex-wrap gap-y-2 gap-x-4">
                  <div className="flex">
                    <Box className="bg-white-200 p-2 flex items-center gap-2">
                      <CarIcon strokeWidth={1.5} />

                      <p className="text-sm font-bold">
                        {objective.system.display_name}
                      </p>
                    </Box>
                  </div>

                  <div className="flex flex-col items-start">
                    <p className="font-bold text-base">
                      {objective.action.display_name} <span className="font-normal">em</span> {objective.part.display_name}
                    </p>

                    <p className="text-xs">
                      {objective.action.description}
                    </p>
                  </div>
                </div>
              )}
              body={(
                <div className="flex flex-col gap-3">
                  <DiagnosticEntryObjectivePieces
                    objective={objective}
                    dataUpdatedAt={forwardingState?.dataUpdatedAt}
                  />

                  <DiagnosticEntryObjectiveActivities
                    objective={objective}
                    dataUpdatedAt={forwardingState?.dataUpdatedAt}
                  />
                </div>
              )}
            />
          ))}
        </div>
      </Box>

      <Box className="flex flex-col gap-4 p-4">
        <DirectiveEntryDiagnosticEntryPending />
      </Box>
    </>
  );
}

export default DirectiveEntryDiagnosticEntryCompleted;
